import React, { useMemo, useRef, useState } from "react";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCServicesApi, RCCTooltip, RCCSelectOptionsCard } from "react-common-components";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import AppIcons from "src/components/AppIcons";
import ButtonExpandGroupers from "src/components/ButtonExpandGroupers";
import CheckPermission from "src/components/CheckPermission";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import FiltersTagsContainer from "src/components/FiltersTagsContainer";
import { IRefFiltersTagsContainer, TagsTypes } from "src/components/FiltersTagsContainer/interface";
import Loader from "src/components/Loader";
import LovGrouperMeeting from "src/components/Lov/GrouperMeetings";
import LovMaps from "src/components/Lov/Maps";
import LovMeetingRooms from "src/components/Lov/MeetingRooms";
import LovMeetingTypes from "src/components/Lov/MeetingTypes";
import LovUsers from "src/components/Lov/Users";
import PageTitle from "src/components/PageTitle";

import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import { getCookie, setCookie } from "src/utils/cookies";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import { getLocalStorage, setLocalStorage } from "src/utils/localStorage";
import stringToDate from "src/utils/stringToDate";
import ScreenStandardGroup from "src/Interfaces/screenStandardGroup";
import GrouperOfMeetings from "./components/grouperOfMeetings";
import ModalFormMeeting from "./components/modalFormMeeting";
import { IFiltersMeetingPanel, IGroupersMeeting, IRefModalFormMeeting, MeetingGroupers } from "./interface";

const MeetingPanel: React.FC = () => {
	const params = useParams<{
        meetingId: string;
    }>();
	
	const { addToast } = RCCHookToast();
	const { translate } = useTranslation();
	const { user } = useAuth();

	const [groupers, setGroupers] = useState<IGroupersMeeting[]>([]);
	const [defaultGrouper, setDefaultGrouper] = useState<MeetingGroupers | undefined>(getCookie(ScreenStandardGroup.MEETINGS) as MeetingGroupers);
	
	const [layoutList, setLayoutList] = useState<"card" | "list">(getLocalStorage(`MeetingScreenLayoutList-${user.efa005Id}`) === "card" ? "card" : "list");
	const [groupersSelect, setGroupersSelect] = useState<MeetingGroupers>(defaultGrouper || MeetingGroupers.MEETING_ROOM);
	const [filters, setFilters] = useState<IFiltersMeetingPanel>({
		meetingId: params.meetingId || ""
	} as IFiltersMeetingPanel);
	const [myMeetings, setMyMeetings] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [showTagsFilters, setShowTagsFilters] = useState(false);

	const filtersTagsRef = useRef<IRefFiltersTagsContainer>(null);
	const buttonSubmitFormGroupersRef = useRef<HTMLButtonElement>(null);
	const refmodalForm = useRef<IRefModalFormMeeting>(null);

	const {
		handleSubmit : handleSubmitFilters, 
		control: controlFilters, 
		register : registerFilters, 
		reset: resetFilters, 
		setValue: setValueFilters 
	} = useForm();

	const { 
		handleSubmit : handleSubmitGroupers, 
		control: controlGroupers 
	} = useForm();

	const tryParseString = (obj: any) => {
		if(Array.isArray(obj))
			return obj.map((x:any) => x.value).join();
		if(typeof obj === "object")
			return obj.value.toString();
		if(typeof obj === "number")
			return obj.toString();
		if(typeof obj !== "string")
			return "";
		return obj ?? "";
	};

	const areUsingFilter = ():boolean => !!filters?.filter || !!filters?.endDate || !!filters?.map || !!filters?.meetingRoom || !!filters?.meetingType|| !!filters?.responsible || !!filters?.startDate;

	const submitGroupers = async (data: any) => {
		setGroupersSelect(data.group);
	};

	const submitFilters = (data: any) => {
		setFilters(() => {	
			const newState = {} as  IFiltersMeetingPanel;
			newState.filter = tryParseString(data.filter);
			newState.meetingType =  tryParseString(data.meetingType);
			newState.meetingRoom = tryParseString(data.meetingRoom);
			newState.map= tryParseString(data.map);
			newState.responsible = tryParseString(data.responsible);
			newState.participants = tryParseString(data.participants);

			if(data.startDate)
				newState.startDate = formatDate(data.startDate);
			if(data.endDate)
				newState.endDate = formatDate(data.endDate);
			
			newState.meetingId = "";
			
			return newState;
		});	
	};
	
	const submitFiltersTags = async () => {
		setFilters((prevState) => {
			const updateState = {...prevState};
			updateState.tags = filtersTagsRef.current?.response ?? "";
			return updateState;
		});
	};

	const setAsDefaultGrouper = async () => {
		try{
			await RCCServicesApi.postRequest({
				method: "Meetings/SetDefaultGrouper",
				body: {
					group:groupersSelect
				}
			});
			setDefaultGrouper(groupersSelect);
			setCookie(ScreenStandardGroup.MEETINGS, groupersSelect);
			addToast({
				color: "green",
				description: translate("Agrupador padrão atualizado com sucesso"),
				title: translate("Sucesso")
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao alterar agrupador padrão"), addToast);
		}
	};

	const { errorFetching, loading, loadData } = useEffectFetchData<IGroupersMeeting[]> (
		"Meetings/ListGroupers", { 
			group:groupersSelect,
			...filters
		},
		({ data }) => {
			setGroupers(data);
		},
		[groupersSelect, filters],
		true
	);

	
	const Groupers = useMemo(() => (
		<>
			{errorFetching ? (
				<RCCAlert 
					color="red"
					title={translate("Atenção")}
					description={translate("Erro ao carregar dados")}
				/>
			): 
				<>
					{loading ? 
						<Loader /> : 
						<>
							{groupers.length === 0 ? (
								<RCCAlert 
									title={translate("Atenção")}
									description={translate("Nenhum agrupador encontrado")}
								/>
							): (
								<>
									{groupers.map((grouper) => 
										<GrouperOfMeetings
											key={`${grouper.id}-${grouper.id2}-grouper-meetings`}
											grouper={grouper}
											filters={filters}
											loadGroupers={loadData}
											layoutList={layoutList}	groupersSelect={groupersSelect}
											modalRef={refmodalForm}
										/>
									)}
								</>
							)}
						</>
					}
				</>
			}	
		</>
	), [groupers, layoutList, loading, errorFetching]);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Reuniões")}
					icon={() => <AppIcons name="meeting" />}
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Nova reunião")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								size="md"
								onClick={() => refmodalForm.current?.actionButtonAdd(() => loadData())}
							/>
						</RCCTooltip>
					</CheckPermission>
					<RCCTooltip
						text={layoutList === "card" ? translate("Alterar para lista") : translate("Alterar para cards")}
						color="grey"
					>
						<RCCButton
							icon={{
								name: layoutList === "card" ? "list" : "table", 
								color: "#FFF", 
								size: 16
							}}
							color="grey"
							layout="isRounded"
							size="md"
							onClick={() => {
								if(layoutList === "card"){
									setLayoutList("list");
									setLocalStorage(`MeetingScreenLayoutList-${user.efa005Id}`, "list");
									return;
								}
								setLocalStorage(`MeetingScreenLayoutList-${user.efa005Id}`, "card");
								setLayoutList("card");
							}}
						/>
					</RCCTooltip>

					<RCCTooltip
						text={`${translate("Filtros")} Tags`}
						color={showTagsFilters ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "tags", color: "#FFF", size: 16}}
							color={showTagsFilters ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowTagsFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>

					<RCCTooltip
						text={translate("Filtros")}
						color={ showFilters || areUsingFilter() ? "blue" : "grey" }
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={ showFilters || areUsingFilter() ? "blue" : "grey" }
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
					<ButtonExpandGroupers />	
				</PageTitle>		

				{/* Filters */}			
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						filter: "",
						meetingType: {value : "", label: ""},
						map: {value : "", label: ""},
						meetingRoom: {value : "", label: ""},
						startDate: null,
						endDate : null ,
						responsible: {value : "", label: ""},
						participants: []
					})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						spacing={1}
						styleInline="margin-bottom: 2.4rem !important;"
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}	
						>
							<RCCInput
								name="filter" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters?.filter}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}	
						>
							<LovMeetingRooms 
								control={controlFilters}
								name="meetingRoom"
								label={translate("Sala")}
								defaultValue={filters?.meetingRoom}
								isClearable	
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}	
						>
							<LovMeetingTypes 
								control={controlFilters}
								name="meetingType"
								label={translate("Tipo de reunião")}
								defaultValue={filters?.meetingType}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
						>
							<LovMaps 
								control={controlFilters}
								name="map"
								label={translate("Mapa")}
								defaultValue={filters?.map}
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<DateTimePickerRange
								control={controlFilters}
								nameInit = "startDate"
								nameEnd = "endDate"
								label = {translate("Data")}
								labelSeparator = {translate("até")}
								type = "datePicker"
								defaultValueInit ={stringToDate(filters?.startDate)}
								defaultValueEnd ={stringToDate(filters?.endDate)}
								setValue={setValueFilters}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovUsers 
								control={controlFilters}
								name="responsible"
								label={translate("Responsável")}
								defaultValue={ filters?.responsible}
								disabled={myMeetings}
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovUsers 
								control={controlFilters}
								name="participants"
								label={translate("Participantes")}
								defaultValue={ filters?.participants}
								disabled={myMeetings}
								isClearable
								isMulti
							/>
						</RCCGrid>
					</RCCGrid>
					
				</FiltersContainer>

				{/* Filters Tags */}
				<FiltersTagsContainer 
					ref={filtersTagsRef}
					onSubmitFilters={submitFiltersTags}
					tagType={TagsTypes.MEETINGS}
					showFilters={showTagsFilters}
				/>

				<form onSubmit={handleSubmitGroupers(submitGroupers)} style={{marginBottom: "1rem"}}>
					<RCCGrid
						container
						item
						spacing={1}
						justifyContent="space-between"
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							alignItems="baseline"
						>
							<LovGrouperMeeting 
								control={controlGroupers}
								name="group"
								onChangeCapture={() => buttonSubmitFormGroupersRef.current?.click()}
								defaultValue={groupersSelect}
								styleInline="width: auto; flex: 1;"
							/>
							<RCCTooltip
								color="grey"
								text={translate("Definir como agrupador padrão")}
							>
								<RCCButton 
									color="transparent"
									icon={{
										name: "checkBookmark",
										color: "var(--grey)"
									}}
									onClick={setAsDefaultGrouper}
								/>
							</RCCTooltip>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={6}
							lg={3}
							justifyContent="flex-end"
						>
							<RCCSelectOptionsCard
								defaultValue={myMeetings ? "S" : "N"}
								options={[{
									value: "N",
									label: translate("Todas as reuniões")
								},{
									value: "S",
									label: translate("Minhas reuniões")
								}]}
								label="&nbsp;"
								onChange={(e) => {
									setValueFilters("responsible", { value: "", label: ""});
									setValueFilters("participants", []);
									if(e === "S"){
										setMyMeetings(true);
										setFilters((prevState) => ({
											...prevState,
											responsible: user.efa005Id.toString(),
											participants:user.efa005Id.toString()
										}));
										return;
									}
									setMyMeetings(false);
									setFilters((prevState) => ({
										...prevState,
										responsible: "",
										participants:""
									}));
								}}
							/>
						</RCCGrid>
					</RCCGrid>
					
					<button 
						aria-label="Submit Form"
						ref={buttonSubmitFormGroupersRef}
						type="submit" 
						style={{ display: "none" }}
					></button>
				</form>
							
				{Groupers} 
               
				<ModalFormMeeting ref={refmodalForm}/>				
			</RCCGrid>
		</>
	);
};

export default MeetingPanel;
